/**
 * VavClient — the whole client, minus the UI.
 *
 * Owns the identity, the ratchet sessions, the local message store and the
 * socket. Both the web app and the mobile app drive this same object; the only
 * thing they supply is a `KeyValueStore` and (on RN) a WebSocket factory.
 */
import {
  DEFAULT_PREKEY_COUNT,
  NoSessionError,
  UntrustedIdentityError,
  createIdentity,
  createOutboundSession,
  deviceRegion,
  discoveryCacheKey,
  hashPhone,
  hashPhonesAsync,
  normalisePhone,
  createSignedPreKey,
  generateDHKeyPair,
  decodePayload,
  decryptAttachment,
  encodePayload,
  encryptAttachment,
  deserializeSession,
  fromB64,
  newId,
  openMessage,
  publicOneTimePreKeys,
  publicSignedPreKey,
  replenishOneTimePreKeys,
  rotateSignedPreKey,
  safetyNumber,
  sealMessage,
  serializeSession,
  sign,
  toB64,
  utf8,
  type AttachmentMeta,
  type AttachmentRef,
  type CallSignal,
  type Ciphertext,
  type Envelope,
  type IdentityState,
  type MessageContent,
  type MessagePayload,
  type ProfileUpdateContent,
  type SessionRecord,
} from '@vav/protocol';

import {
  Api,
  ApiError,
  type DeviceSummary,
  type InviteRecord,
  type InviteSummary,
  type UserSummary,
} from './api.ts';
import {
  decodeOffer,
  encodeOffer,
  linkVerificationCode,
  openLinkPayload,
  sealLinkPayload,
  type LinkOffer,
  type LinkPayload,
} from './linking.ts';
import { SocketClient, type ConnectionStatus, type WebSocketFactory } from './socket.ts';
import { deserializeIdentity, serializeIdentity } from './identityStore.ts';
import { STORAGE_KEYS, type Account, type Contact, type Conversation, type Message } from './model.ts';
import type { KeyValueStore } from './storage.ts';

const REGISTER_CONTEXT = 'VAV-Register-v1';
const LOGIN_CONTEXT = 'VAV-Login-v1';
/** Refill one-time prekeys when the server's pool drops below this. */
const PREKEY_LOW_WATER = 20;
const SPK_ROTATION_MS = 7 * 24 * 60 * 60 * 1000;
/** How long a user's device list is trusted before re-checking. */
const DEVICE_CACHE_MS = 60_000;
/**
 * How much history a sync bundle carries per conversation. Recent context is
 * what a new device actually needs; the full archive stays where it was
 * decrypted. Media still opens everywhere, because refs carry their own keys.
 */
const SYNC_MESSAGES_PER_CONVERSATION = 300;
/** Set once a device has either supplied its own history or imported one. */
const SYNC_DONE_KEY = 'sync.done';

interface DeviceTarget {
  userId: string;
  deviceId: number;
}

/**
 * Whether a query should also be tried as a phone number.
 *
 * Mostly digits with the punctuation people actually type, and long enough to
 * be a real number — so "dana" is never hashed and "052-123-4567" always is.
 */
function looksLikePhone(query: string): boolean {
  const digits = query.replace(/\D/g, '');
  return digits.length >= 7 && /^[+\d][\d\s().-]*$/.test(query);
}

/** Sessions are per device; this is the map/storage key. */
function sessionKey(userId: string, deviceId: number): string {
  return `${userId}:${deviceId}`;
}

export interface VavEvents {
  message: (message: Message) => void;
  'message-updated': (message: Message) => void;
  /**
   * A contact changed — their name, their picture, their keys.
   *
   * Without this the rename bug happens exactly as it was reported: the
   * profile-update arrives, the contact is saved, and the old name stays on
   * screen until something unrelated re-renders. Storage was right and the UI
   * was stale, which from outside is indistinguishable from "it never worked".
   */
  contact: (contact: Contact) => void;
  /**
   * A message is gone from this device. Distinct from 'message-updated' with a
   * `deleted` tombstone, which is what "delete for everyone" produces and which
   * every device is meant to show as "this message was deleted". Nothing is
   * left to show here, so the row goes.
   */
  'message-removed': (event: { conversationId: string; messageId: string }) => void;
  conversation: (conversation: Conversation) => void;
  status: (status: ConnectionStatus) => void;
  typing: (event: { conversationId: string; userId: string; isTyping: boolean }) => void;
  presence: (event: { userId: string; online: boolean; lastSeen?: number }) => void;
  'identity-change': (event: { userId: string; newIdentityKey: string }) => void;
  /**
   * This account changed.
   *
   * The account is a plain object on the client, so anything reading
   * `client.account` will happily keep showing the old value: renaming updated
   * the profile screen and left the name in the sidebar untouched. React has to
   * be told.
   */
  account: (account: Account) => void;
  /**
   * A step of a call arrived.
   *
   * Delivered as an event rather than written to history: signalling is
   * machinery, not conversation, and a thread full of ICE candidates would be
   * both useless and enormous. Whether a call leaves any trace in the
   * conversation is the UI's decision.
   */
  'call-signal': (event: {
    conversationId: string;
    from: string;
    callId: string;
    signal: CallSignal;
  }) => void;
  'conversation-removed': (event: { conversationId: string }) => void;
  'contact-removed': (event: { userId: string }) => void;
  error: (error: Error) => void;
}

type Listener = (...args: never[]) => void;

export interface VavClientOptions {
  serverUrl: string;
  store: KeyValueStore;
  webSocketFactory?: WebSocketFactory;
}

/**
 * The number could not be parsed.
 *
 * Almost always means a national-format number with no region to interpret it
 * against, so the UI should suggest the international form rather than implying
 * the number is wrong.
 */
/**
 * The person you are writing to has deleted their account.
 *
 * A named error rather than a message, because the UI has to do something about
 * it -- stop offering to send -- and matching on prose is how that quietly
 * breaks in the next language.
 */
export class RecipientGoneError extends Error {
  readonly code = 'recipient_gone';
  /** Written out rather than a parameter property: Node's type stripping, which
      is how these sources run, does not support them. */
  readonly userId: string;

  constructor(userId: string) {
    super(`recipient ${userId} is no longer registered`);
    this.name = 'RecipientGoneError';
    this.userId = userId;
  }
}

export class InvalidPhoneError extends Error {
  readonly code = 'invalid_phone';
  constructor() {
    super('invalid_phone');
    this.name = 'InvalidPhoneError';
  }
}

export class VavClient {
  #store: KeyValueStore;
  #api: Api;
  #serverUrl: string;
  #socket?: SocketClient;
  #wsFactory?: WebSocketFactory;

  #account?: Account;
  #identity?: IdentityState;
  #sessions = new Map<string, SessionRecord>();
  #contacts = new Map<string, Contact>();
  #conversations = new Map<string, Conversation>();
  #listeners = new Map<keyof VavEvents, Set<Listener>>();
  /** Envelope ids waiting to be acked, batched to avoid a frame per message. */
  #pendingAcks: string[] = [];
  #ackTimer?: ReturnType<typeof setTimeout>;
  /**
   * Serialises inbound processing.
   *
   * Ratchet state is inherently sequential — decrypting message N advances the
   * chain that message N+1 depends on. Handling a burst concurrently interleaves
   * those mutations and delivers messages out of order, so every envelope waits
   * for the previous one to finish.
   */
  #inbound: Promise<void> = Promise.resolve();
  /** Short-lived cache of each user's device list, refreshed before sends. */
  #deviceCache = new Map<string, { deviceIds: number[]; at: number }>();

  constructor(options: VavClientOptions) {
    this.#store = options.store;
    this.#serverUrl = options.serverUrl.replace(/\/$/, '');
    this.#api = new Api(this.#serverUrl);
    this.#wsFactory = options.webSocketFactory;
  }

  // -------------------------------------------------------------------------
  // Events
  // -------------------------------------------------------------------------

  on<K extends keyof VavEvents>(event: K, listener: VavEvents[K]): () => void {
    const set = this.#listeners.get(event) ?? new Set<Listener>();
    set.add(listener as Listener);
    this.#listeners.set(event, set);
    return () => set.delete(listener as Listener);
  }

  #emit<K extends keyof VavEvents>(event: K, ...args: Parameters<VavEvents[K]>): void {
    for (const listener of this.#listeners.get(event) ?? []) {
      try {
        (listener as (...a: unknown[]) => void)(...args);
      } catch (err) {
        // A throwing UI listener must not abort message processing.
        console.error(`[vav] listener for "${event}" threw`, err);
      }
    }
  }

  // -------------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------------

  get account(): Account | undefined {
    return this.#account;
  }
  get connectionStatus(): ConnectionStatus {
    return this.#socket?.status ?? 'offline';
  }
  get isRegistered(): boolean {
    return Boolean(this.#account && this.#identity);
  }

  /** Load persisted state. Returns true when a usable account was found. */
  async load(): Promise<boolean> {
    const [rawAccount, rawIdentity] = await Promise.all([
      this.#store.get(STORAGE_KEYS.account),
      this.#store.get(STORAGE_KEYS.identity),
    ]);
    if (!rawAccount || !rawIdentity) return false;

    this.#account = JSON.parse(rawAccount) as Account;
    this.#identity = deserializeIdentity(JSON.parse(rawIdentity));
    this.#api = new Api(this.#account.serverUrl, this.#account.token);
    this.#serverUrl = this.#account.serverUrl;

    for (const key of await this.#store.keys(STORAGE_KEYS.sessionPrefix)) {
      const raw = await this.#store.get(key);
      if (raw) {
        const session = deserializeSession(JSON.parse(raw));
        this.#sessions.set(sessionKey(session.peerUserId, session.peerDeviceId), session);
      }
    }
    for (const key of await this.#store.keys(STORAGE_KEYS.contactPrefix)) {
      const raw = await this.#store.get(key);
      if (raw) {
        const contact = JSON.parse(raw) as Contact;
        this.#contacts.set(contact.id, contact);
      }
    }
    for (const key of await this.#store.keys(STORAGE_KEYS.conversationPrefix)) {
      const raw = await this.#store.get(key);
      if (raw) {
        const conversation = JSON.parse(raw) as Conversation;
        this.#conversations.set(conversation.id, conversation);
      }
    }
    return true;
  }

  /**
   * Create an account from a username alone.
   *
   * No phone number is taken here, and none should ever be added. A phone is an
   * optional discovery field, attached afterwards via `setDiscoverablePhone` and
   * removable with a single null, never a condition of entry. Encourage linking
   * one; never require it.
   *
   * Usernames are also what makes an account portable: they survive SIM changes
   * and number portability, and they let someone be reachable without handing
   * out a personal number.
   */
  async register(username: string, displayName: string, inviteCode?: string): Promise<Account> {
    const identity = createIdentity('pending', 1, DEFAULT_PREKEY_COUNT);
    const proof = utf8(`${REGISTER_CONTEXT}:${username.toLowerCase()}:${identity.deviceId}`);

    const res = await this.#api.register({
      username,
      displayName,
      deviceId: identity.deviceId,
      identityKey: toB64(identity.identity.pub),
      registrationId: identity.registrationId,
      signedPreKey: publicSignedPreKey(identity.signedPreKey),
      oneTimePreKeys: publicOneTimePreKeys(identity),
      signature: toB64(sign(identity.identity.priv, proof)),
      inviteCode,
    });

    this.#identity = { ...identity, userId: res.userId };
    this.#account = {
      userId: res.userId,
      deviceId: res.deviceId,
      username: res.username,
      displayName,
      token: res.token,
      serverUrl: this.#serverUrl,
    };
    this.#api.token = res.token;

    await this.#persistIdentity();
    await this.#store.set(STORAGE_KEYS.account, JSON.stringify(this.#account));

    // Arriving through an invite should not land you in an empty app: open the
    // conversation with whoever invited you, so there is someone to talk to.
    if (res.invitedBy) {
      await this.startDirectConversation({
        id: res.invitedBy.id,
        username: res.invitedBy.username,
        displayName: res.invitedBy.displayName,
        createdAt: Date.now(),
      }).catch(() => {
        // A failure here costs a convenience, not the account.
      });
    }

    return this.#account;
  }

  /**
   * Re-authenticate an existing device.
   *
   * Only works where the identity private key already lives — there is no
   * password to type on a new device, by design. Moving to a new device means
   * registering a new identity, and contacts will see the safety number change.
   */
  async login(): Promise<void> {
    if (!this.#identity || !this.#account) throw new Error('no local identity to log in with');
    const { userId, nonce } = await this.#api.challenge(this.#account.username);
    const message = utf8(`${LOGIN_CONTEXT}:${nonce}:${userId}:${this.#account.deviceId}`);
    const res = await this.#api.login({
      userId,
      deviceId: this.#account.deviceId,
      nonce,
      signature: toB64(sign(this.#identity.identity.priv, message)),
    });

    this.#account = { ...this.#account, token: res.token };
    this.#api.token = res.token;
    await this.#store.set(STORAGE_KEYS.account, JSON.stringify(this.#account));
  }

  async connect(): Promise<void> {
    if (!this.#account) throw new Error('not registered');

    this.#socket = new SocketClient(
      this.#serverUrl.replace(/^http/, 'ws'),
      this.#account.token,
      {
        onEnvelope: (envelope) => this.#enqueueEnvelope(envelope),
        onQueueDrained: () => this.#flushAcks(),
        onTyping: (e) => this.#emit('typing', e),
        onPresence: (e) => {
          this.#emit('presence', e);
          void this.#touchContact(e.userId, { lastSeen: e.lastSeen });
        },
        onStatus: (status) => {
          this.#emit('status', status);
          if (status === 'online') void this.#maintainKeys();
        },
      },
      this.#wsFactory,
    );
    this.#socket.connect();
    this.#socket.watch([...this.#contacts.keys()]);
    // Names heal themselves on every connect; see the method for why this is
    // not redundant with the profile-update broadcast.
    void this.#refreshContactNames();
    // And conversations that never received my profile get it now.
    void this.#healProfileShares();
    // And contacts whose picture never arrived get asked for it.
    void this.#requestMissingProfiles();
    // And a freshly linked, still-empty device asks a sibling for the store.
    void this.#requestSyncIfEmpty();
  }

  /**
   * Pull every contact's current display name from the server.
   *
   * The broadcast covers the live case, but a broadcast is a moment: a rename
   * made before the feature existed, or while a contact's account was briefly
   * unreachable, is a moment missed for ever — which is exactly the report
   * "the picture arrived but the name did not". Unlike the picture, the name
   * is server-visible by design, so the authoritative copy is always one GET
   * away. Fetching it on connect turns every missed broadcast into a
   * self-healing stale read instead of a permanent one.
   */
  async #refreshContactNames(): Promise<void> {
    for (const contact of [...this.#contacts.values()]) {
      try {
        const user = await this.#api.getUser(contact.id);
        if (user.displayName && user.displayName !== contact.displayName) {
          await this.#saveContact({ ...contact, displayName: user.displayName });
        }
      } catch {
        // Deleted account, or offline — either way the stored name stands.
      }
    }
  }

  disconnect(): void {
    this.#flushAcks();
    this.#socket?.close();
    this.#socket = undefined;
  }

  /** Forget everything on this device. Messages already delivered are gone. */
  async logout(): Promise<void> {
    this.disconnect();
    await this.#store.clear();
    this.#account = undefined;
    this.#identity = undefined;
    this.#sessions.clear();
    this.#contacts.clear();
    this.#conversations.clear();
  }

  // -------------------------------------------------------------------------
  // Multi-device
  // -------------------------------------------------------------------------

  /** Devices currently attached to this account. */
  async listMyDevices(): Promise<DeviceSummary[]> {
    if (!this.#account) throw new Error('not registered');
    const { devices } = await this.#api.listDevices(this.#account.userId);
    return devices;
  }

  async removeDevice(deviceId: number): Promise<void> {
    await this.#api.removeDevice(deviceId);
    // Drop the session so a device re-linked under the same id starts fresh
    // rather than reusing a ratchet the removed device also knows.
    this.#sessions.delete(sessionKey(this.#account!.userId, deviceId));
    await this.#store.remove(STORAGE_KEYS.session(this.#account!.userId, deviceId));
    this.#deviceCache.delete(this.#account!.userId);
  }

  /**
   * New device, step 1: ask the server for a linking session and return the
   * code to display. Nothing secret is in the code — only a public key.
   */
  async beginLinking(): Promise<LinkOffer> {
    const ephemeral = generateDHKeyPair();
    const { provisionId, expiresAt } = await this.#api.startProvision(toB64(ephemeral.pub));
    return {
      provisionId,
      ephemeral,
      code: encodeOffer(provisionId, ephemeral.pub),
      expiresAt,
    };
  }

  /**
   * New device, step 2: wait for the primary to approve, then adopt the
   * account identity and register as a new device.
   */
  async completeLinking(offer: LinkOffer, signal?: { aborted: boolean }): Promise<Account> {
    for (;;) {
      if (signal?.aborted) throw new Error('linking cancelled');
      if (Date.now() > offer.expiresAt) throw new Error('linking code expired');

      const result = await this.#api.pollProvision(offer.provisionId);
      if (result.pending) {
        await new Promise((r) => setTimeout(r, 1500));
        continue;
      }

      const payload = openLinkPayload(offer.ephemeral, result.senderPub, result.ciphertext);
      return this.#adoptLinkedIdentity(payload);
    }
  }

  /**
   * Primary side: approve a code the user scanned or typed.
   *
   * `expectedCode` is the six digits shown on the new device. Comparing them
   * is what stops an attacker who substituted their own QR from walking away
   * with the account.
   */
  async approveLinking(code: string, expectedCode?: string): Promise<void> {
    if (!this.#account || !this.#identity) throw new Error('not registered');
    const { provisionId, ephemeralPub } = decodeOffer(code);

    if (expectedCode && linkVerificationCode(ephemeralPub) !== expectedCode) {
      throw new Error('the codes do not match — do not approve this device');
    }

    const sealed = sealLinkPayload(
      {
        userId: this.#account.userId,
        username: this.#account.username,
        displayName: this.#account.displayName,
        identityPriv: toB64(this.#identity.identity.priv),
        identityPub: toB64(this.#identity.identity.pub),
        serverUrl: this.#account.serverUrl,
      },
      ephemeralPub,
    );
    await this.#api.approveProvision(provisionId, { ...sealed, ephemeralPub: toB64(ephemeralPub) });
  }

  /** Turn a decrypted link payload into a working local account. */
  async #adoptLinkedIdentity(payload: LinkPayload): Promise<Account> {
    // Fresh prekeys for this device — sharing the primary's would mean two
    // devices racing to consume the same one-time keys.
    const fresh = createIdentity(payload.userId, 1, DEFAULT_PREKEY_COUNT);
    const identity: IdentityState = {
      ...fresh,
      identity: { priv: fromB64(payload.identityPriv), pub: fromB64(payload.identityPub) },
    };
    // The signed prekey must be signed by the *account* key, not the throwaway
    // one createIdentity generated.
    identity.signedPreKey = createSignedPreKey(identity.identity, 1);

    this.#serverUrl = payload.serverUrl;
    this.#api = new Api(payload.serverUrl);

    const { userId, nonce } = await this.#api.challenge(payload.username);
    // deviceId 0 marks "no device yet", distinguishing this from a login.
    const message = utf8(`${LOGIN_CONTEXT}:${nonce}:${userId}:0`);

    const { deviceId, token } = await this.#api.registerDevice({
      userId: payload.userId,
      identityKey: payload.identityPub,
      registrationId: identity.registrationId,
      signedPreKey: publicSignedPreKey(identity.signedPreKey),
      oneTimePreKeys: publicOneTimePreKeys(identity),
      nonce,
      signature: toB64(sign(identity.identity.priv, message)),
    });

    this.#identity = { ...identity, deviceId };
    this.#account = {
      userId: payload.userId,
      deviceId,
      username: payload.username,
      displayName: payload.displayName,
      token,
      serverUrl: payload.serverUrl,
    };
    this.#api.token = token;

    await this.#persistIdentity();
    await this.#store.set(STORAGE_KEYS.account, JSON.stringify(this.#account));
    return this.#account;
  }

  // -------------------------------------------------------------------------
  // Invites
  // -------------------------------------------------------------------------

  /**
   * Create a link that both installs the app and says who is inviting.
   *
   * `baseUrl` is where the app is served from — the link has to be openable by
   * someone who does not have it yet, so it cannot be an app-only scheme.
   *
   * It defaults to this client's own server because that is the one address
   * known to exist: the server serves the web client from `VAV_WEB_DIR`, so in
   * any real deployment its origin *is* the app's origin. The mobile client
   * used to hardcode `https://vav.chat`, which nothing is deployed to, so every
   * link it produced was dead on arrival. A caller that genuinely knows better
   * — the web client, which is already being served from somewhere — passes it.
   */
  async createInviteLink(
    baseUrl?: string,
    options: { maxUses?: number; ttlDays?: number } = {},
  ): Promise<{ code: string; url: string; expiresAt: number }> {
    const { code, expiresAt } = await this.#api.createInvite(options);
    const base = (baseUrl ?? this.#serverUrl).replace(/\/$/, '');
    return { code, expiresAt, url: `${base}/?invite=${code}` };
  }

  /** Who is behind an invite code, for the signup screen to show. */
  async resolveInvite(code: string): Promise<InviteSummary | undefined> {
    try {
      const { inviter } = await this.#api.resolveInvite(code);
      return inviter;
    } catch {
      // Expired, used up, or simply wrong — the signup screen carries on.
      return undefined;
    }
  }

  listInvites(): Promise<{ invites: InviteRecord[] }> {
    return this.#api.listInvites();
  }

  async revokeInvite(code: string): Promise<void> {
    await this.#api.revokeInvite(code);
  }

  // -------------------------------------------------------------------------
  // Contact discovery
  // -------------------------------------------------------------------------

  /**
   * Make this account findable by phone number.
   *
   * The number is hashed here and never sent. That does not make lookups
   * private — see the note in the protocol's discovery module — but it does
   * mean a stolen database is not a phone book.
   */
  async setDiscoverablePhone(phone: string | null): Promise<void> {
    if (phone === null) {
      await this.#api.setDiscoveryPhone(null);
      return;
    }
    const { pepper, defaultRegion } = await this.#api.discoveryConfig();
    const normalised = normalisePhone(phone, deviceRegion() ?? defaultRegion);
    // A code, not a sentence. This reaches the user through the UI, and prose
    // thrown from here bypasses the translation catalogue entirely -- which is
    // exactly how an English string ended up on a Hebrew screen.
    if (!normalised) throw new InvalidPhoneError();
    await this.#api.setDiscoveryPhone(hashPhone(normalised, pepper));
  }

  /**
   * Which of these numbers already have an account.
   *
   * Hashing happens locally and in batches; the caller passes raw numbers from
   * the address book and gets back the ones that matched, paired with the
   * original string so the UI can show the name the user knows.
   *
   * The hashing is deliberately expensive — see `hashPhonesAsync` — so a whole
   * address book takes tens of seconds and a caller that shows no progress looks
   * hung. `onProgress` fires per number and `signal` is checked between them, so
   * closing the screen actually stops the work rather than leaving it grinding.
   *
   * Results are cached on this device between runs, so importing the same
   * address book twice only pays for the numbers that are new. The cache holds
   * digests rather than numbers — see `discoveryCacheKey` — because the address
   * book itself is never written down.
   */
  async findContactsByPhone(
    phones: string[],
    options: {
      onProgress?: (done: number, total: number) => void;
      onScanned?: (unique: number, entries: number) => void;
      signal?: { aborted: boolean };
    } = {},
  ): Promise<{ phone: string; user: UserSummary }[]> {
    const { pepper, defaultRegion, maxBatch } = await this.#api.discoveryConfig();

    const cached = await this.#loadDiscoveryCache(pepper);
    const fresh = new Map<string, string>();

    const byHash = await hashPhonesAsync(phones, pepper, deviceRegion() ?? defaultRegion, {
      ...options,
      cache: {
        get: (key) => cached.get(key),
        set: (key, hash) => {
          cached.set(key, hash);
          fresh.set(key, hash);
        },
      },
    });

    // Written even when the import was cancelled: the numbers already hashed
    // cost real time, and throwing that away would make a resumed import start
    // from nothing.
    if (fresh.size > 0) await this.#saveDiscoveryCache(pepper, cached);

    const hashes = [...byHash.keys()];
    const found: { phone: string; user: UserSummary }[] = [];

    for (let i = 0; i < hashes.length; i += maxBatch) {
      if (options.signal?.aborted) break;
      const { matches } = await this.#api.lookupPhones(hashes.slice(i, i + maxBatch));
      for (const match of matches) {
        const phone = byHash.get(match.phoneHash);
        if (!phone) continue;
        found.push({
          phone,
          user: {
            id: match.userId,
            username: match.username,
            displayName: match.displayName,
            createdAt: 0,
          },
        });
      }
    }
    return found;
  }

  /**
   * The discovery hash cache, as one blob.
   *
   * Keyed by a fingerprint of the pepper: rotating it on the server makes every
   * stored hash meaningless, and a stale cache would then match nobody while
   * looking like it was working. A new fingerprint simply starts empty.
   *
   * One blob rather than a key per number because an address book is tens of
   * thousands of entries and this store is not a database.
   */
  async #loadDiscoveryCache(pepper: string): Promise<Map<string, string>> {
    try {
      const raw = await this.#store.get(STORAGE_KEYS.discoveryCache(pepperFingerprint(pepper)));
      if (!raw) return new Map();
      return new Map(Object.entries(JSON.parse(raw) as Record<string, string>));
    } catch {
      // A corrupt cache is a performance problem, never a correctness one.
      return new Map();
    }
  }

  async #saveDiscoveryCache(pepper: string, entries: Map<string, string>): Promise<void> {
    try {
      await this.#store.set(
        STORAGE_KEYS.discoveryCache(pepperFingerprint(pepper)),
        JSON.stringify(Object.fromEntries(entries)),
      );
    } catch {
      // Out of quota, most likely. The next import is slow again, which is worse
      // than it needs to be but not broken.
    }
  }

  // -------------------------------------------------------------------------
  // Push
  // -------------------------------------------------------------------------

  /** What the server can offer this platform, e.g. the public VAPID key. */
  pushConfig(): Promise<{ enabled: boolean; web: { vapidPublicKey: string } | null }> {
    return this.#api.pushConfig();
  }

  /**
   * Hand the server a handle it can use to wake this device.
   *
   * The push itself carries no content — the server has none. When it arrives
   * the app connects, drains the queue and decrypts locally before showing
   * anything, which is why a notification can say who a message is from while
   * the server that triggered it cannot.
   */
  async enablePush(platform: 'expo' | 'web', token: string): Promise<void> {
    await this.#api.setPushToken(platform, token);
  }

  async disablePush(): Promise<void> {
    await this.#api.clearPushToken();
  }

  // -------------------------------------------------------------------------
  // Key maintenance
  // -------------------------------------------------------------------------

  /**
   * Top up one-time prekeys and rotate the signed prekey when it ages out.
   *
   * If the pool runs dry, new contacts fall back to a bundle without a one-time
   * key — still secure, but with weaker forward secrecy, so it is worth keeping
   * stocked.
   */
  async #maintainKeys(): Promise<void> {
    if (!this.#identity) return;
    try {
      const { count } = await this.#api.keyCount();
      const payload: Parameters<Api['uploadKeys']>[0] = {};

      if (count < PREKEY_LOW_WATER) {
        const { state, added } = replenishOneTimePreKeys(this.#identity, DEFAULT_PREKEY_COUNT - count);
        this.#identity = state;
        payload.oneTimePreKeys = added;
      }
      if (Date.now() - this.#identity.signedPreKey.createdAt > SPK_ROTATION_MS) {
        this.#identity = rotateSignedPreKey(this.#identity);
        payload.signedPreKey = publicSignedPreKey(this.#identity.signedPreKey);
      }
      if (payload.oneTimePreKeys || payload.signedPreKey) {
        await this.#api.uploadKeys(payload);
        await this.#persistIdentity();
      }
    } catch (err) {
      // Non-fatal: messaging keeps working on the existing keys.
      this.#emit('error', err as Error);
    }
  }

  // -------------------------------------------------------------------------
  // Contacts and conversations
  // -------------------------------------------------------------------------

  get contacts(): Contact[] {
    return [...this.#contacts.values()];
  }

  get conversations(): Conversation[] {
    return [...this.#conversations.values()].sort((a, b) => b.lastMessageAt - a.lastMessageAt);
  }

  getConversation(id: string): Conversation | undefined {
    return this.#conversations.get(id);
  }

  getContact(id: string): Contact | undefined {
    return this.#contacts.get(id);
  }

  /**
   * One search box for everything: username, display name, or phone number.
   *
   * A phone-shaped query is hashed here and sent as a digest alongside the
   * text, so typing a number finds the person without the number itself ever
   * reaching the server.
   */
  async searchUsers(query: string): Promise<UserSummary[]> {
    const trimmed = query.trim();
    let phoneHash: string | undefined;

    if (looksLikePhone(trimmed)) {
      try {
        const { pepper, defaultRegion } = await this.#api.discoveryConfig();
        const normalised = normalisePhone(trimmed, deviceRegion() ?? defaultRegion);
        if (normalised) phoneHash = hashPhone(normalised, pepper);
      } catch {
        // Discovery unavailable — fall back to a plain text search.
      }
    }

    const { users } = await this.#api.searchUsers(trimmed, phoneHash);
    return users;
  }

  async startDirectConversation(user: UserSummary): Promise<Conversation> {
    await this.#saveContact({ id: user.id, username: user.username, displayName: user.displayName });
    const existing = this.#conversations.get(user.id);
    if (existing) return existing;
    const conversation = await this.#saveConversation({
      id: user.id,
      kind: 'dm',
      peerId: user.id,
      memberIds: [user.id],
      // No title on purpose: a person's name comes from the contact record,
      // which follows their renames. Freezing it here is how the sidebar kept
      // showing the name from the day you met.
      lastMessageAt: Date.now(),
      unread: 0,
    });
    await this.#shareProfileWith(conversation);
    return conversation;
  }

  async createGroup(name: string, memberIds: string[]): Promise<Conversation> {
    const { groupId, memberIds: members, createdAt } = await this.#api.createGroup(memberIds);
    const conversation = await this.#saveConversation({
      id: groupId,
      kind: 'group',
      groupId,
      memberIds: members,
      title: name,
      lastMessageAt: createdAt,
      unread: 0,
    });
    // Tell the members what the group is called: the server never learns it.
    await this.#sendContent(conversation, { kind: 'group-update', name, addMembers: members });
    return conversation;
  }

  async loadMessages(conversationId: string, limit = 200): Promise<Message[]> {
    const keys = await this.#store.keys(STORAGE_KEYS.messagePrefix(conversationId));
    const slice = keys.slice(-limit);
    const messages: Message[] = [];
    for (const key of slice) {
      const raw = await this.#store.get(key);
      if (!raw) continue;
      const message = JSON.parse(raw) as Message;
      // Builds from before the unknown-kind guard stored housekeeping
      // envelopes -- a profile-request from a newer peer -- as messages, and
      // they render as empty bubbles. Filtering here, not just at arrival,
      // retroactively cleans every thread that was polluted while stale.
      const kind = (message.content as { kind: string }).kind;
      if (kind !== 'text' && kind !== 'media') continue;
      messages.push(message);
    }
    return messages;
  }

  // -------------------------------------------------------------------------
  // Sending
  // -------------------------------------------------------------------------

  /**
   * Write now, send later.
   *
   * The envelopes are sealed at this moment and handed to the server with a
   * time before which it must not deliver them. The server learns nothing new
   * -- it already sees sender, recipient and a timestamp on every envelope --
   * and the recipient sees an ordinary message arrive at the chosen time.
   *
   * The one honest limitation: the words are frozen now. What is sent at the
   * scheduled time is what was written at this one.
   */
  async scheduleText(conversationId: string, text: string, sendAt: number): Promise<Message> {
    const conversation = this.#requireConversation(conversationId);
    const message = await this.#sendContent(
      conversation,
      { kind: 'text', text },
      undefined,
      true,
      sendAt,
    );
    // Flip the local copy once the moment passes, so the clock badge does not
    // outlive the send. Best-effort: after a reload the badge may linger until
    // the thread reloads, which costs a stale glyph and nothing else.
    const timer = setTimeout(() => {
      void this.#updateMessage(conversationId, message.id, (m) =>
        m.state === 'scheduled' ? { ...m, state: 'sent' } : m,
      );
    }, Math.max(0, sendAt - Date.now()) + 500);
    timer.unref?.();
    return message;
  }

  /**
   * Withdraw a scheduled message before its time.
   *
   * Server first, then the local copy: if the server refuses -- the time
   * already came -- the message is on its way and the local copy must stay,
   * because pretending to unsend is worse than not offering to.
   */
  async cancelScheduled(conversationId: string, messageId: string): Promise<void> {
    const messages = await this.loadMessages(conversationId);
    const target = messages.find((m) => m.id === messageId);
    if (!target || target.state !== 'scheduled' || !target.scheduledEnvelopeIds?.length) {
      throw new Error('not a scheduled message');
    }
    const { cancelled } = await this.#api.unschedule(target.scheduledEnvelopeIds);
    if (cancelled.length === 0) throw new Error('already sent');
    await this.deleteMessageForMe(conversationId, messageId);
  }

  /**
   * Change the words of a message already sent.
   *
   * Same trust model as delete-for-everyone: a sealed request each recipient
   * honours. The result is labelled — a conversation is a record two people
   * hold, and rewriting it silently is worse than admitting the change.
   */
  /**
   * Change a group's name or picture.
   *
   * Rides the existing group-update — the same sealed channel that named the
   * group at creation, so the server never sees either. The picture is
   * encrypted exactly like a profile picture.
   *
   * Any member can do this, and that is a property of the sealed design rather
   * than a choice: the server cannot read the update to enforce a role, and
   * recipients apply group-updates from any member. Restricting it to the
   * creator would need client-side role checks all round — worth doing when
   * groups grow past friends.
   */
  /**
   * Leave a group: tell the members, tell the server, then forget it here.
   * The order matters — the goodbye has to be sealed and sent while we are
   * still a member with working fan-out.
   */
  async leaveGroup(conversationId: string): Promise<void> {
    const conversation = this.#requireConversation(conversationId);
    if (!conversation.groupId || !this.#account) throw new Error('not a group');
    try {
      await this.#sendContent(
        conversation,
        { kind: 'group-update', removeMembers: [this.#account.userId] },
        undefined,
        false,
      );
    } catch {
      // Every other member may be gone; leaving must still work.
    }
    await this.#api.removeGroupMember(conversation.groupId, this.#account.userId).catch(() => undefined);
    await this.removeConversation(conversationId);
  }

  /**
   * Forget a conversation on this device: its row, its history. Purely local —
   * the other side's copy is theirs, exactly like account deletion.
   */
  async removeConversation(conversationId: string): Promise<void> {
    const keys = await this.#store.keys(STORAGE_KEYS.messagePrefix(conversationId));
    for (const key of keys) await this.#store.remove(key);
    await this.#store.remove(STORAGE_KEYS.conversation(conversationId));
    const conversation = this.#conversations.get(conversationId);
    this.#conversations.delete(conversationId);
    if (conversation) this.#emit('conversation', { ...conversation, unread: 0 });
    this.#emit('conversation-removed', { conversationId });
  }

  /**
   * Delete a contact: their record, their DM and its history — from this
   * device. Nothing is sent; un-knowing someone is not their business.
   */
  async deleteContact(userId: string): Promise<void> {
    await this.removeConversation(userId);
    await this.#store.remove(STORAGE_KEYS.contact(userId));
    this.#contacts.delete(userId);
    this.#emit('contact-removed', { userId });
  }

  async updateGroup(
    conversationId: string,
    changes: { name?: string; avatar?: { data: Uint8Array; meta: AttachmentMeta } },
  ): Promise<void> {
    const conversation = this.#requireConversation(conversationId);
    if (!conversation.groupId) throw new Error('not a group');

    let avatarRef: AttachmentRef | undefined;
    if (changes.avatar) {
      const { blob, ref } = encryptAttachment(changes.avatar.data, changes.avatar.meta);
      const { mediaId } = await this.#api.uploadMedia(blob);
      avatarRef = { ...ref, mediaId };
    }
    const name = changes.name?.trim() || undefined;
    if (!name && !avatarRef) return;

    await this.#sendContent(
      conversation,
      { kind: 'group-update', name, avatar: avatarRef },
      undefined,
      false,
    );
    await this.#saveConversation({
      ...conversation,
      title: name ?? conversation.title,
      avatar: avatarRef ?? conversation.avatar,
    });
  }

  async editMessage(conversationId: string, targetId: string, text: string): Promise<void> {
    const next = text.trim();
    if (!next) throw new Error('an edit cannot leave a message empty');
    const messages = await this.loadMessages(conversationId);
    const target = messages.find((m) => m.id === targetId);
    if (!target || !target.outgoing) throw new Error('only your own messages can be edited');
    if (!('text' in target.content) || target.content.kind !== 'text') {
      throw new Error('only text can be edited');
    }
    const conversation = this.#requireConversation(conversationId);
    await this.#sendContent(conversation, { kind: 'edit', targetId, text: next }, undefined, false);
    await this.#applyEdit(conversationId, targetId, next, this.#account!.userId);
  }

  /**
   * Change a scheduled message before it leaves.
   *
   * The envelopes were sealed at scheduling, so "edit" is withdraw-and-reseal:
   * the held envelopes are cancelled and the new words are sealed for the same
   * moment. Server-first — if the time already came, the old words are on
   * their way and this fails rather than pretends.
   */
  async editScheduled(conversationId: string, messageId: string, text: string): Promise<Message> {
    const next = text.trim();
    if (!next) throw new Error('an edit cannot leave a message empty');
    const messages = await this.loadMessages(conversationId);
    const target = messages.find((m) => m.id === messageId);
    if (!target || target.state !== 'scheduled' || !target.scheduledFor || !target.scheduledEnvelopeIds?.length) {
      throw new Error('not a scheduled message');
    }
    const { cancelled } = await this.#api.unschedule(target.scheduledEnvelopeIds);
    if (cancelled.length === 0) throw new Error('already sent');
    const sendAt = target.scheduledFor;
    await this.deleteMessageForMe(conversationId, messageId);
    return this.scheduleText(conversationId, next, sendAt);
  }

  async #applyEdit(
    conversationId: string,
    targetId: string,
    text: string,
    editorId: string,
  ): Promise<void> {
    await this.#updateMessage(conversationId, targetId, (message) =>
      // Only the author may rewrite their words. Without this check an edit
      // from anyone in a group would rewrite anyone's message — forgery, not
      // editing.
      message.senderId === editorId && 'text' in message.content
        ? { ...message, content: { ...message.content, text }, edited: true }
        : message,
    );
  }

  async sendText(conversationId: string, text: string, replyTo?: string): Promise<Message> {
    const conversation = this.#requireConversation(conversationId);
    return this.#sendContent(conversation, { kind: 'text', text }, replyTo);
  }

  async sendMedia(
    conversationId: string,
    data: Uint8Array,
    meta: AttachmentMeta,
    caption?: string,
  ): Promise<Message> {
    const conversation = this.#requireConversation(conversationId);
    // Encrypt before upload: the server only ever sees the sealed blob.
    const { blob, ref } = encryptAttachment(data, meta);
    const { mediaId } = await this.#api.uploadMedia(blob);
    const attachment: AttachmentRef = { ...ref, mediaId };
    return this.#sendContent(conversation, { kind: 'media', text: caption, attachment });
  }

  async downloadAttachment(attachment: AttachmentRef): Promise<Uint8Array> {
    return decryptAttachment(await this.#api.downloadMedia(attachment.mediaId), attachment);
  }

  async react(conversationId: string, targetId: string, emoji: string, remove = false): Promise<void> {
    const conversation = this.#requireConversation(conversationId);
    await this.#sendContent(conversation, { kind: 'reaction', targetId, emoji, remove }, undefined, false);
    await this.#applyReaction(conversationId, targetId, emoji, this.#account!.userId, remove);
  }

  /**
   * Delete for everyone: ask the other devices to tombstone it too.
   *
   * This is a request, not a guarantee. A recipient who is offline forever, or
   * running something other than this client, keeps their copy — end-to-end
   * encryption means there is no server-side copy to reach in and remove. Only
   * offer this for messages the user sent, where the request is at least
   * plausible.
   */
  async deleteMessage(conversationId: string, targetId: string): Promise<void> {
    const conversation = this.#requireConversation(conversationId);
    await this.#sendContent(conversation, { kind: 'delete', targetId }, undefined, false);
    await this.#applyDelete(conversationId, targetId);
  }

  /**
   * Delete for me: drop it from this device and tell nobody.
   *
   * The counterpart to the above, and the only kind of deletion that is
   * honest about what it can do. It works on messages the user received as
   * well as ones they sent, because "I do not want this on my phone" is a
   * different need from "I regret sending this", and the second one cannot
   * serve the first.
   *
   * The row is removed rather than tombstoned: a "this was deleted" placeholder
   * is a message about someone else's action, and there is no one else here.
   */
  async deleteMessageForMe(conversationId: string, messageId: string): Promise<void> {
    const keys = await this.#store.keys(STORAGE_KEYS.messagePrefix(conversationId));
    const key = keys.find((k) => k.endsWith(`:${messageId}`));
    if (!key) return;
    await this.#store.remove(key);
    this.#emit('message-removed', { conversationId, messageId });
  }

  /**
   * Send an existing message on to another conversation.
   *
   * Media is forwarded by reference, not re-uploaded: the `AttachmentRef`
   * already carries the key the recipient needs, so the blob on the server is
   * reused as it stands. That also means forwarding a photo hands its key to
   * whoever receives it — which is what forwarding *is*, and worth being clear
   * about rather than discovering later.
   *
   * Reads the stored message rather than taking content from the caller, so
   * only things a user actually has can be forwarded, and only the two kinds
   * that mean anything out of context. A receipt or a reaction refers to a
   * message id in a conversation the recipient cannot see.
   */
  async forwardMessage(
    fromConversationId: string,
    messageId: string,
    toConversationId: string,
  ): Promise<Message> {
    const target = this.#requireConversation(toConversationId);

    const messages = await this.loadMessages(fromConversationId);
    const source = messages.find((message) => message.id === messageId);
    if (!source) throw new Error(`no message ${messageId}`);
    if (source.deleted) throw new Error('cannot forward a deleted message');

    const content = source.content;
    if (content.kind !== 'text' && content.kind !== 'media') {
      throw new Error(`cannot forward ${content.kind}`);
    }

    // Deliberately not carrying `replyTo`: it points at a message in the
    // conversation this is leaving, which the recipient has never seen.
    return this.#sendContent(target, content);
  }

  /**
   * Send one step of a call.
   *
   * `to` addresses a single member; every other member ignores it. A group call
   * is a mesh of two-party negotiations, and an offer meant for one person is
   * meaningless to the rest — applying it would corrupt their own leg.
   *
   * Invisible, like receipts and reactions: nothing is stored, and no
   * conversation is bumped up the list because someone's ICE candidate arrived.
   */
  async sendCallSignal(
    conversationId: string,
    callId: string,
    signal: CallSignal,
    to?: string,
  ): Promise<void> {
    const conversation = this.#requireConversation(conversationId);
    await this.#sendContent(conversation, { kind: 'call', callId, to, signal }, undefined, false);
  }

  /** ICE servers for a call, minted fresh so TURN credentials are short-lived. */
  fetchIceServers(): Promise<{ iceServers: unknown[]; relay: boolean }> {
    return this.#api.iceServers();
  }

  /**
   * Change the name everyone sees.
   *
   * The local account is updated only after the server has accepted it, so a
   * failed call leaves the two agreeing rather than showing a name the server
   * never took.
   */
  async setDisplayName(displayName: string): Promise<void> {
    const name = displayName.trim();
    if (!name) throw new Error('display name cannot be empty');
    if (!this.#account) throw new Error('not registered');

    const result = await this.#api.updateProfile(name);
    this.#account = { ...this.#account, displayName: result.displayName };
    await this.#store.set(STORAGE_KEYS.account, JSON.stringify(this.#account));
    this.#emit('account', this.#account);
    // The server serves the new name to anyone who looks you up; this reaches
    // the people who already know you and would otherwise keep the old one
    // forever.
    await this.#broadcastProfile({ displayName: result.displayName });
  }

  /**
   * Fan my current profile out to everyone I talk to.
   *
   * One sealed `profile-update` per direct conversation. Group members who are
   * not also direct contacts are deliberately not reached — a profile is shared
   * with the people you talk to, and being in the same group is not that.
   */
  async #broadcastProfile(update: Omit<ProfileUpdateContent, 'kind'>): Promise<void> {
    const dms = [...this.#conversations.values()].filter((c) => c.kind === 'dm');
    for (const conversation of dms) {
      try {
        await this.#sendContent(conversation, { kind: 'profile-update', ...update }, undefined, false);
      } catch {
        // One unreachable contact must not stop the rest from learning. A
        // deleted account in the list would otherwise abort the whole loop.
      }
    }
  }

  /**
   * A new direct conversation starts blind: my picture was broadcast when I
   * set it, and a conversation that did not exist yet could not hear that
   * broadcast. So the profile is shared once at creation, from both ends --
   * whoever starts the conversation tells, and whoever discovers it on the
   * receiving side answers. Two people who have just met see faces, not the
   * initials of whoever set their picture first.
   */
  async #shareProfileWith(conversation: Conversation): Promise<void> {
    if (!this.#account) return;
    const update: Omit<ProfileUpdateContent, 'kind'> = { displayName: this.#account.displayName };
    if (this.#account.avatar) update.avatar = this.#account.avatar;
    try {
      await this.#sendContent(conversation, { kind: 'profile-update', ...update }, undefined, false);
      await this.#saveConversation({ ...conversation, profileShared: true });
    } catch (err) {
      // A deleted recipient cancels the debt rather than deferring it: there
      // is nobody left to share with, and retrying on every connect is how
      // the same failure resurfaced forever.
      if (err instanceof RecipientGoneError) {
        const current = this.#conversations.get(conversation.id) ?? conversation;
        await this.#saveConversation({ ...current, profileShared: true });
        return;
      }
      // Anything else is a courtesy deferred: the unset flag retries on the
      // next connect.
    }
  }

  /**
   * Share my profile into every direct conversation that has never had it.
   *
   * The share-at-creation hook covers conversations born after the feature;
   * this covers the ones born before it -- like a new profile that already
   * messaged a contact and left both sides on initials. Runs on connect, does
   * each conversation once ever, and the flag is only set when the send
   * actually went through.
   */
  async #healProfileShares(): Promise<void> {
    const dms = [...this.#conversations.values()].filter((c) => c.kind === 'dm' && !c.profileShared);
    for (const conversation of dms) {
      await this.#shareProfileWith(conversation);
    }
  }

  // ---------------------------------------------------------------------------
  // Device-to-device store sync
  // ---------------------------------------------------------------------------

  /** Seal one payload to every *other* device of this account. */
  async #sendToOwnDevices(content: MessageContent): Promise<void> {
    if (!this.#identity || !this.#account) return;
    const me = this.#account;
    // The device that just linked is the whole audience, and a cache from a
    // minute ago does not know it exists yet. Sync traffic is rare; fetch.
    this.#deviceCache.delete(me.userId);
    const plaintext = encodePayload({ id: newId(), ts: Date.now(), content });
    const outgoing = [];
    for (const deviceId of await this.#devicesOf(me.userId)) {
      if (deviceId === me.deviceId) continue;
      const session = await this.#sessionWith(me.userId, deviceId);
      outgoing.push({
        toUserId: me.userId,
        toDeviceId: deviceId,
        ciphertext: sealMessage(this.#identity.identity.pub, session, plaintext),
      });
      await this.#persistSession(session);
    }
    // Sibling housekeeping never rings a phone.
    if (outgoing.length) await this.#api.sendMessages(outgoing, undefined, 'silent');
  }

  #syncRequestedAt = 0;

  /**
   * A linked device that has never completed a sync asks its siblings.
   *
   * This is what turns a linked device into the account: contacts, group
   * names, the conversation list and recent history all live only on the
   * other devices, because the server has never seen any of them readable.
   * Keyed on a stored marker rather than on "is the store empty" — a device
   * that chatted a little before the sync feature existed is not empty, but
   * its history gap is exactly as real. The original device marks itself done
   * at once; a linked one asks on every connect until one bundle lands.
   * Queued like any envelope, so a sleeping sibling answers when it wakes.
   */
  async #requestSyncIfEmpty(): Promise<void> {
    if (!this.#account) return;
    if (await this.#store.get(SYNC_DONE_KEY)) return;
    if (this.#account.deviceId === 1) {
      // The original device: there is no elder sibling to inherit from.
      await this.#store.set(SYNC_DONE_KEY, String(Date.now()));
      return;
    }
    if (Date.now() - this.#syncRequestedAt < 60_000) return;
    this.#syncRequestedAt = Date.now();
    const deviceIds = await this.#devicesOf(this.#account.userId).catch(() => []);
    if (deviceIds.length <= 1) return;
    await this.#sendToOwnDevices({ kind: 'sync-request' }).catch(() => undefined);
  }

  async #onSyncContent(content: { kind: 'sync-request' } | { kind: 'sync-bundle'; ref: AttachmentRef }): Promise<void> {
    if (content.kind === 'sync-request') {
      // Only a device that actually has something answers; the requester
      // itself is empty and must not reply to a sibling's request with nothing.
      if (this.#conversations.size === 0) return;
      await this.#sendSyncBundle();
      return;
    }
    await this.#importSyncBundle(content.ref);
    // One good bundle settles it; connects stop asking.
    await this.#store.set(SYNC_DONE_KEY, String(Date.now()));
  }

  /** Everything worth carrying, as one encrypted attachment. */
  async #sendSyncBundle(): Promise<void> {
    const conversations = [...this.#conversations.values()];
    const messages: Record<string, Message[]> = {};
    for (const conversation of conversations) {
      messages[conversation.id] = await this.loadMessages(conversation.id, SYNC_MESSAGES_PER_CONVERSATION);
    }
    const bundle = { contacts: this.contacts, conversations, messages };
    const { blob, ref } = encryptAttachment(utf8(JSON.stringify(bundle)), {
      mime: 'application/json',
      name: 'sync.json',
    });
    const { mediaId } = await this.#api.uploadMedia(blob);
    await this.#sendToOwnDevices({ kind: 'sync-bundle', ref: { ...ref, mediaId } });
  }

  /**
   * Merge a sibling's bundle. Local data always wins — the bundle fills gaps,
   * it never overwrites, so a second bundle arriving from a third device (or
   * the same one twice) changes nothing that is already here.
   */
  async #importSyncBundle(ref: AttachmentRef): Promise<void> {
    const bytes = await this.downloadAttachment(ref);
    const bundle = JSON.parse(new TextDecoder().decode(bytes)) as {
      contacts?: Contact[];
      conversations?: Conversation[];
      messages?: Record<string, Message[]>;
    };

    for (const contact of bundle.contacts ?? []) {
      const existing = this.#contacts.get(contact.id);
      await this.#saveContact(
        existing ? { ...contact, ...existing, avatar: existing.avatar ?? contact.avatar } : contact,
      );
    }

    for (const conversation of bundle.conversations ?? []) {
      const existing = this.#conversations.get(conversation.id);
      if (!existing) {
        // Fresh flags: this device shares its own profile and counts its own
        // unread. What carries over is the thing only siblings know — the
        // conversation itself, its title, its picture.
        await this.#saveConversation({
          ...conversation,
          unread: 0,
          profileShared: undefined,
          profileRequestedAt: undefined,
        });
      } else {
        // Fill the gaps a live-only device has: a title it never learned, a
        // picture, and the preview line — a thread whose history just arrived
        // must not keep saying "no messages yet".
        const merged = {
          ...existing,
          title: existing.title ?? conversation.title,
          avatar: existing.avatar ?? conversation.avatar,
          lastPreview: existing.lastPreview ?? conversation.lastPreview,
          lastMessageAt: Math.max(existing.lastMessageAt ?? 0, conversation.lastMessageAt ?? 0),
        };
        if (JSON.stringify(merged) !== JSON.stringify(existing)) {
          await this.#saveConversation(merged);
        }
      }

      for (const message of bundle.messages?.[conversation.id] ?? []) {
        const key = STORAGE_KEYS.message(conversation.id, message.ts, message.id);
        if (await this.#store.get(key)) continue;
        await this.#store.set(key, JSON.stringify(message));
      }
      const saved = this.#conversations.get(conversation.id);
      if (saved) this.#emit('conversation', saved);
    }
  }

  /**
   * Ask for what is missing, from the side that knows it is missing.
   *
   * Sharing at creation and healing on connect both run on the *other* side,
   * and both mark themselves done when their send succeeds. One envelope lost
   * after that -- a decrypt hiccup, a store cleared during the white-screen
   * era, a profile switched away mid-write -- and this side sits on initials
   * for ever, because nobody anywhere retries. This is that retry: one tiny
   * sealed request per picture-less contact per connect, answered by the
   * other side with an ordinary profile-update. The field report it exists
   * for: the same contact showing a face in one of Chen's profiles and
   * initials in the other.
   */
  async #requestMissingProfiles(): Promise<void> {
    // At most one ask per conversation per six hours. A peer on a build too
    // old to answer renders the request as an empty bubble in their thread,
    // and an app that reconnects on every network flap would write them a
    // wall of nothing.
    const REQUEST_EVERY = 6 * 60 * 60 * 1000;
    for (const conversation of [...this.#conversations.values()]) {
      if (conversation.kind !== 'dm' || !conversation.peerId) continue;
      const contact = this.#contacts.get(conversation.peerId);
      if (!contact || contact.avatar) continue;
      if (Date.now() - (conversation.profileRequestedAt ?? 0) < REQUEST_EVERY) continue;
      try {
        await this.#sendContent(conversation, { kind: 'profile-request' }, undefined, false);
        const current = this.#conversations.get(conversation.id) ?? conversation;
        await this.#saveConversation({ ...current, profileRequestedAt: Date.now() });
      } catch {
        // Unreachable peer; the next connect asks again.
      }
    }
  }

  /**
   * Set, or clear, my profile picture.
   *
   * The picture never reaches the server readable: it is encrypted exactly like
   * a message attachment, the sealed blob is uploaded, and the ref carrying the
   * key travels to each contact through the sealed channel — the same shape as
   * a group avatar, which is the model this copies. The server stores a face it
   * cannot open.
   *
   * The cost is the intended one: strangers see initials. Whoever can see your
   * face is someone you talk to.
   */
  async setAvatar(data: Uint8Array, meta: AttachmentMeta): Promise<void> {
    if (!this.#account) throw new Error('not registered');
    const { blob, ref } = encryptAttachment(data, meta);
    const { mediaId } = await this.#api.uploadMedia(blob);
    const avatar: AttachmentRef = { ...ref, mediaId };

    this.#account = { ...this.#account, avatar };
    await this.#store.set(STORAGE_KEYS.account, JSON.stringify(this.#account));
    this.#emit('account', this.#account);
    await this.#broadcastProfile({ avatar });
  }

  async clearAvatar(): Promise<void> {
    if (!this.#account) throw new Error('not registered');
    this.#account = { ...this.#account, avatar: undefined };
    await this.#store.set(STORAGE_KEYS.account, JSON.stringify(this.#account));
    this.#emit('account', this.#account);
    await this.#broadcastProfile({ clearAvatar: true });
  }

  /**
   * Delete this account, everywhere it can be deleted.
   *
   * The server drops everything it holds; this device drops its keys. What
   * cannot be reached is anyone else's copy of the conversation, and that is
   * not a gap to apologise for -- their history is theirs.
   *
   * Contacts are not told. They find out when they next try to write, because
   * the key bundle is gone by then. Announcing it would broadcast the fact of
   * leaving to everyone who ever spoke to this account.
   */
  async deleteAccount(): Promise<void> {
    await this.#api.deleteAccount();
    await this.logout();
  }

  async markRead(conversationId: string): Promise<void> {
    const conversation = this.#conversations.get(conversationId);
    if (!conversation || conversation.unread === 0) return;

    const messages = await this.loadMessages(conversationId, 100);
    const unreadIds = messages.filter((m) => !m.outgoing && m.state !== 'read').map((m) => m.id);
    await this.#saveConversation({ ...conversation, unread: 0 });
    if (unreadIds.length) {
      try {
        await this.#sendContent(
          conversation,
          { kind: 'receipt', type: 'read', messageIds: unreadIds },
          undefined,
          false,
        );
      } catch {
        // A read receipt that cannot be delivered — offline, or the sender's
        // account is gone — changes nothing for the reader. Marking read is
        // the job; the receipt is a courtesy to the other side.
      }
    }
  }

  setTyping(conversationId: string, isTyping: boolean): void {
    const conversation = this.#conversations.get(conversationId);
    if (!conversation) return;
    this.#socket?.setTyping(conversationId, this.#recipientsOf(conversation), isTyping);
  }

  /**
   * Seal `content` once per recipient and hand the batch to the server.
   *
   * `visible` is false for control traffic (receipts, reactions, deletes) that
   * should travel encrypted but never appear as a bubble in the thread.
   */
  async #sendContent(
    conversation: Conversation,
    content: MessageContent,
    replyTo?: string,
    visible = true,
    deliverAfter?: number,
  ): Promise<Message> {
    if (!this.#identity || !this.#account) throw new Error('not registered');

    const payload: MessagePayload = {
      id: newId(),
      // A scheduled message is stamped with the time it will leave, so it sits
      // in both threads where it will actually happen, not where it was typed.
      ts: deliverAfter ?? Date.now(),
      groupId: conversation.groupId,
      replyTo,
      // Only meaningful to our own devices; a peer already knows the thread.
      syncTo: conversation.groupId ? undefined : conversation.id,
      content,
    };

    const message: Message = {
      id: payload.id,
      conversationId: conversation.id,
      senderId: this.#account.userId,
      outgoing: true,
      ts: payload.ts,
      content,
      state: 'pending',
      replyTo,
    };
    if (visible) {
      await this.#saveMessage(message);
      this.#emit('message', message);
    }

    try {
      const plaintext = encodePayload(payload);
      const { targets, gone } = await this.#targetsFor(conversation);

      /**
       * Everyone this was for has deleted their account.
       *
       * Reported rather than sent into the void: the envelope would be
       * addressed to nobody and the message would sit in the thread looking
       * delivered. In a group the others still receive it -- one person leaving
       * is not a reason to stop the conversation.
       */
      if (gone.length > 0 && targets.every((t) => t.userId === this.#account!.userId)) {
        await this.#markPeerGone(conversation);
        throw new RecipientGoneError(gone[0]);
      }

      const outgoing = [];
      for (const target of targets) {
        const session = await this.#sessionWith(target.userId, target.deviceId);
        outgoing.push({
          toUserId: target.userId,
          toDeviceId: target.deviceId,
          groupId: conversation.groupId,
          ciphertext: sealMessage(this.#identity.identity.pub, session, plaintext),
        });
        await this.#persistSession(session);
      }
      let heldIds: string[] = [];
      if (outgoing.length) {
        // What the doorbell may say — the deliberate metadata bits beyond
        // routing, because a frozen app is steered only by its push. 'call'
        // makes the push ring. 'silent' sends no push at all: receipts,
        // reactions and profile housekeeping can wait for the next open, and
        // waking a phone with "new message" for a read receipt is how Chen's
        // pocket buzzed all day over nothing.
        const pushHint =
          content.kind === 'call'
            ? content.signal.type === 'ring'
              ? ('call' as const)
              : ('silent' as const)
            : content.kind === 'text' || content.kind === 'media'
              ? undefined
              : ('silent' as const);
        const result = await this.#api.sendMessages(outgoing, deliverAfter, pushHint);
        if (deliverAfter) heldIds = result.accepted.map((a) => a.id);
        /**
         * The server took nothing for anyone this was meant for.
         *
         * The device cache means we can address an envelope to someone who
         * deleted their account minutes ago and never notice; the server is the
         * only party that knows for certain. In a group the others still got
         * it, so only a send with no live recipient at all is an error.
         */
        const skipped = new Set(result.skipped ?? []);
        if (skipped.size > 0) {
          for (const userId of skipped) this.#deviceCache.delete(userId);
          const intended = this.#recipientsOf(conversation);
          if (intended.length > 0 && intended.every((id) => skipped.has(id))) {
            await this.#markPeerGone(conversation);
            throw new RecipientGoneError(intended[0]);
          }
        }
      }

      if (visible) {
        const sent: Message = deliverAfter
          ? { ...message, state: 'scheduled', scheduledFor: deliverAfter, scheduledEnvelopeIds: heldIds }
          : { ...message, state: 'sent' };
        await this.#saveMessage(sent);
        await this.#saveConversation({
          ...conversation,
          lastMessageAt: payload.ts,
          lastPreview: previewOf(content),
        });
        this.#emit('message-updated', sent);
        return sent;
      }
      return message;
    } catch (err) {
      if (visible) {
        const failed: Message = { ...message, state: 'failed' };
        await this.#saveMessage(failed);
        this.#emit('message-updated', failed);
      }
      // A deleted recipient is an outcome, not an incident: the thrown error
      // reaches whoever asked for the send, and the UI shows its localised
      // notice there. Emitting it globally too is how a background heal at a
      // dead conversation put a raw English toast on Chen's sidebar on every
      // connect, forever.
      if (!(err instanceof RecipientGoneError)) this.#emit('error', err as Error);
      throw err;
    }
  }

  /** The user ids a message in this conversation is addressed to. */
  #recipientsOf(conversation: Conversation): string[] {
    const me = this.#account?.userId;
    if (conversation.kind === 'dm') return conversation.peerId ? [conversation.peerId] : [];
    return conversation.memberIds.filter((id) => id !== me);
  }

  /**
   * Expand recipients into individual devices, and add our own other devices.
   *
   * The self-fan-out is what makes a sent message appear on your laptop after
   * you typed it on your phone. Without it the other device has no way to learn
   * about a message it never received — the server cannot help, since it only
   * ever sees ciphertext addressed to someone else.
   */
  async #targetsFor(
    conversation: Conversation,
  ): Promise<{ targets: DeviceTarget[]; gone: string[] }> {
    const me = this.#account!;
    const recipients = this.#recipientsOf(conversation);
    const targets: DeviceTarget[] = [];
    const gone: string[] = [];

    for (const userId of [...recipients, me.userId]) {
      const deviceIds = await this.#devicesOf(userId);
      // An account with no devices left is one that has been deleted. Worth
      // reporting rather than quietly addressing an envelope to nobody.
      if (deviceIds.length === 0 && userId !== me.userId) gone.push(userId);
      for (const deviceId of deviceIds) {
        // Never send to ourselves — that would decrypt into our own ratchet.
        if (userId === me.userId && deviceId === me.deviceId) continue;
        targets.push({ userId, deviceId });
      }
    }
    return { targets, gone };
  }

  /**
   * Device ids for a user, cached briefly.
   *
   * Re-fetched often enough that a newly linked device starts receiving within
   * a minute, but not on every keystroke — this runs before every send.
   */
  async #devicesOf(userId: string): Promise<number[]> {
    const cached = this.#deviceCache.get(userId);
    if (cached && Date.now() - cached.at < DEVICE_CACHE_MS) return cached.deviceIds;

    try {
      const { devices } = await this.#api.listDevices(userId);
      const deviceIds = devices.map((d) => d.deviceId);
      this.#deviceCache.set(userId, { deviceIds, at: Date.now() });
      return deviceIds;
    } catch (err) {
      // Offline, or the user was deleted. Fall back to whatever we knew, and
      // to device 1 so a first message still has somewhere to go.
      if (cached) return cached.deviceIds;
      if (err instanceof ApiError && err.status === 404) return [];
      return [1];
    }
  }

  async #sessionWith(peerUserId: string, peerDeviceId: number): Promise<SessionRecord> {
    const key = sessionKey(peerUserId, peerDeviceId);
    const existing = this.#sessions.get(key);
    if (existing) return existing;

    const { bundles } = await this.#api.getDeviceKeys(peerUserId, peerDeviceId);
    if (bundles.length === 0) {
      throw new Error(`no keys for ${peerUserId}/${peerDeviceId}`);
    }

    // createOutboundSession verifies the bundle signature and throws otherwise.
    const session = createOutboundSession(this.#identity!.identity, bundles[0]);
    this.#sessions.set(key, session);

    // Every device of a user shares one identity key, so this is also the
    // contact's key — recording it is what makes an impostor detectable later.
    if (peerUserId !== this.#account?.userId) {
      await this.#saveContact({
        id: peerUserId,
        username: this.#contacts.get(peerUserId)?.username ?? peerUserId,
        displayName: this.#contacts.get(peerUserId)?.displayName ?? peerUserId,
        identityKey: bundles[0].identityKey,
      });
    }
    return session;
  }

  // -------------------------------------------------------------------------
  // Receiving
  // -------------------------------------------------------------------------

  #enqueueEnvelope(envelope: Envelope): void {
    // Chained rather than awaited so the socket callback stays synchronous;
    // the catch keeps one bad envelope from stalling the whole queue.
    this.#inbound = this.#inbound.then(() => this.#onEnvelope(envelope)).catch(() => {});
  }

  async #onEnvelope(envelope: Envelope): Promise<void> {
    try {
      await this.#processEnvelope(envelope);
    } catch (err) {
      if (err instanceof UntrustedIdentityError) {
        this.#emit('identity-change', {
          userId: err.peerUserId,
          newIdentityKey: toB64(err.newIdentityKey),
        });
      } else if (!(err instanceof NoSessionError)) {
        this.#emit('error', err as Error);
      }
      // A decryption failure that is not an identity dispute means the pair's
      // ratchets have diverged — the classic cause is that device re-linking
      // under a reused id, which leaves every peer sealing to a dead ratchet
      // for ever. Heal instead of just mourning: answer with a fresh key
      // exchange so the next round trip converges.
      if (!(err instanceof UntrustedIdentityError)) {
        void this.#requestSessionReset(envelope.fromUserId, envelope.fromDeviceId).catch(() => undefined);
      }
      // Ack regardless: a message we cannot decrypt will never become
      // decryptable, and leaving it queued would replay the failure forever.
    }
    this.#queueAck(envelope.id);
  }

  /**
   * The discovery that an account is gone becomes a fact on the conversation,
   * so the UI can say it plainly on open rather than only after a failed
   * send. DMs only: a group outlives any one member.
   */
  async #markPeerGone(conversation: Conversation): Promise<void> {
    if (conversation.kind !== 'dm' || conversation.peerGone) return;
    const current = this.#conversations.get(conversation.id) ?? conversation;
    await this.#saveConversation({ ...current, peerGone: true });
  }

  /** Once per pair per window, so a burst of dead envelopes sends one cure. */
  #resetRequestedAt = new Map<string, number>();

  async #requestSessionReset(userId: string, deviceId: number): Promise<void> {
    if (!this.#identity || !this.#account) return;
    const key = sessionKey(userId, deviceId);
    if (Date.now() - (this.#resetRequestedAt.get(key) ?? 0) < 120_000) return;
    this.#resetRequestedAt.set(key, Date.now());

    // Drop our half of the dead pair, build a fresh outbound session, and send
    // an envelope whose only cargo is its own key exchange. Their side opens
    // it, replaces its record, and their next send reaches us again.
    this.#sessions.delete(key);
    await this.#store.remove(STORAGE_KEYS.session(userId, deviceId));
    const session = await this.#sessionWith(userId, deviceId);
    const plaintext = encodePayload({ id: newId(), ts: Date.now(), content: { kind: 'session-reset' } });
    await this.#api.sendMessages(
      [
        {
          toUserId: userId,
          toDeviceId: deviceId,
          ciphertext: sealMessage(this.#identity.identity.pub, session, plaintext),
        },
      ],
      undefined,
      'silent',
    );
    await this.#persistSession(session);
  }

  async #processEnvelope(envelope: Envelope): Promise<void> {
    if (!this.#identity || !this.#account) throw new Error('not registered');

    const key = sessionKey(envelope.fromUserId, envelope.fromDeviceId);
    const known = this.#contacts.get(envelope.fromUserId)?.identityKey;
    const result = openMessage(
      this.#identity,
      this.#sessions.get(key),
      envelope.ciphertext as Ciphertext,
      { userId: envelope.fromUserId, deviceId: envelope.fromDeviceId },
      { knownIdentityKey: known ? fromB64(known) : undefined },
    );

    this.#sessions.set(key, result.record);
    await this.#persistSession(result.record);
    if (result.consumedOneTimePreKeyId !== undefined) {
      delete this.#identity.oneTimePreKeys[result.consumedOneTimePreKeyId];
      await this.#persistIdentity();
    }

    const payload = decodePayload(result.plaintext);
    /**
     * A message from another of our own devices is a sync copy of something
     * *we* sent. Its conversation is therefore the one it was addressed to,
     * which the sender recorded in `syncTo` — using the sender id here would
     * file our own messages into a conversation with ourselves.
     */
    const fromSelf = envelope.fromUserId === this.#account.userId;

    // Device-to-device housekeeping belongs to no conversation; handle it
    // before the conversation plumbing drops it for lacking one. Only our own
    // devices are ever obeyed — a stranger asking for the store is ignored.
    if (payload.content.kind === 'sync-request' || payload.content.kind === 'sync-bundle') {
      if (fromSelf) await this.#onSyncContent(payload.content).catch(() => undefined);
      return;
    }
    // A session reset's whole cargo was its key exchange, consumed above.
    if (payload.content.kind === 'session-reset') return;

    const conversationId = payload.groupId ?? (fromSelf ? (payload.syncTo ?? '') : envelope.fromUserId);
    if (!conversationId) return;

    const peerForConversation = fromSelf ? conversationId : envelope.fromUserId;
    await this.#ensureConversation(conversationId, payload.groupId, peerForConversation);
    await this.#applyIncoming(conversationId, envelope.fromUserId, payload, fromSelf);
  }

  async #applyIncoming(
    conversationId: string,
    senderId: string,
    payload: MessagePayload,
    fromSelf = false,
  ): Promise<void> {
    const content = payload.content;

    switch (content.kind) {
      case 'receipt': {
        for (const id of content.messageIds) {
          await this.#updateMessage(conversationId, id, (m) =>
            m.outgoing ? { ...m, state: content.type } : m,
          );
        }
        return;
      }
      case 'reaction':
        await this.#applyReaction(conversationId, content.targetId, content.emoji, senderId, content.remove);
        return;
      case 'delete':
        await this.#applyDelete(conversationId, content.targetId);
        return;
      case 'edit':
        await this.#applyEdit(conversationId, content.targetId, content.text, senderId);
        return;
      case 'profile-update': {
        if (fromSelf) return;
        const contact = this.#contacts.get(senderId);
        if (!contact) return;
        await this.#saveContact({
          ...contact,
          displayName: content.displayName ?? contact.displayName,
          avatar: content.clearAvatar ? undefined : (content.avatar ?? contact.avatar),
        });
        return;
      }
      case 'profile-request': {
        if (fromSelf) return;
        const conversation = this.#conversations.get(conversationId);
        if (conversation) void this.#shareProfileWith(conversation);
        return;
      }
      case 'call': {
        // Addressed to one member: everyone else drops it. Also drop our own
        // copy, which arrives because a signal fans out to the whole
        // conversation including our other devices.
        if (fromSelf) return;
        if (content.to && content.to !== this.#account?.userId) return;
        this.#emit('call-signal', {
          conversationId,
          from: senderId,
          callId: content.callId,
          signal: content.signal,
        });
        return;
      }
      case 'group-update': {
        const conversation = this.#conversations.get(conversationId);
        if (conversation) {
          await this.#saveConversation({
            ...conversation,
            title: content.name ?? conversation.title,
            avatar: content.avatar ?? conversation.avatar,
            memberIds: [
              ...new Set([
                ...conversation.memberIds.filter((id) => !content.removeMembers?.includes(id)),
                ...(content.addMembers ?? []),
              ]),
            ],
          });
        }
        return;
      }
      default:
        break;
    }

    // Only text and media are messages. Anything else that reaches here is a
    // content kind from a build newer than this one -- storing it would show
    // the peer a junk bubble for every housekeeping envelope a future version
    // invents, which is a strange way to reward not updating yet.
    if ((content as { kind: string }).kind !== 'text' && (content as { kind: string }).kind !== 'media') {
      return;
    }

    const message: Message = {
      id: payload.id,
      conversationId,
      senderId,
      // A synced copy of something we sent from another device is still *our*
      // message: it belongs on the right-hand side of the thread, and it must
      // not count as unread.
      outgoing: fromSelf,
      ts: payload.ts,
      content,
      state: fromSelf ? 'sent' : 'delivered',
      replyTo: payload.replyTo,
    };
    await this.#saveMessage(message);

    const conversation = this.#conversations.get(conversationId)!;
    await this.#saveConversation({
      ...conversation,
      lastMessageAt: Math.max(conversation.lastMessageAt, payload.ts),
      lastPreview: previewOf(content),
      unread: fromSelf ? conversation.unread : conversation.unread + 1,
    });
    this.#emit('message', message);
  }

  async #ensureConversation(id: string, groupId: string | undefined, senderId: string): Promise<void> {
    if (this.#conversations.has(id)) return;

    if (groupId) {
      // A group we have not seen: ask the server who is in it so fan-out works.
      let memberIds = [senderId];
      try {
        const { groups } = await this.#api.listGroups();
        memberIds = groups.find((g) => g.id === groupId)?.members.map((m) => m.userId) ?? memberIds;
      } catch {
        // Offline or forbidden — the group-update message will fill this in.
      }
      await this.#saveConversation({
        id,
        kind: 'group',
        groupId,
        memberIds,
        lastMessageAt: Date.now(),
        unread: 0,
      });
      return;
    }

    // Make sure the contact exists so the UI has a live name to show; the
    // conversation itself carries no title — a person's name must come from
    // the contact record, or a rename can never reach the screen.
    if (!this.#contacts.get(senderId)) await this.#fetchContact(senderId);
    const conversation = await this.#saveConversation({
      id,
      kind: 'dm',
      peerId: senderId,
      memberIds: [senderId],
      lastMessageAt: Date.now(),
      unread: 0,
    });
    // The other side just opened this conversation; answer with who we are.
    await this.#shareProfileWith(conversation);
  }

  async #fetchContact(userId: string): Promise<Contact | undefined> {
    try {
      const user = await this.#api.getUser(userId);
      return this.#saveContact({
        id: user.id,
        username: user.username,
        displayName: user.displayName,
        identityKey: this.#sessions.get(userId)
          ? toB64(this.#sessions.get(userId)!.theirIdentityKey)
          : undefined,
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return undefined;
      throw err;
    }
  }

  // -------------------------------------------------------------------------
  // Verification
  // -------------------------------------------------------------------------

  /**
   * The number both people read aloud to confirm nobody is in the middle.
   * Undefined until we have actually exchanged keys with them.
   */
  safetyNumberWith(userId: string): string | undefined {
    if (!this.#identity) return undefined;
    // Any session with that user will do: all their devices share one identity
    // key, which is precisely why the safety number stays a per-person fact
    // rather than becoming one number per device to compare.
    const stored = this.#contacts.get(userId)?.identityKey;
    const theirKey =
      stored !== undefined
        ? fromB64(stored)
        : [...this.#sessions.values()].find((s) => s.peerUserId === userId)?.theirIdentityKey;

    if (!theirKey) return undefined;
    return safetyNumber(this.#identity.identity.pub, theirKey);
  }

  async markVerified(userId: string, verified: boolean): Promise<void> {
    const contact = this.#contacts.get(userId);
    if (contact) await this.#saveContact({ ...contact, verified });
  }

  /**
   * Accept a peer's new identity key after they reinstalled.
   *
   * Destructive on purpose: the old session is dropped, so anything still
   * in flight under the old keys is lost rather than silently mis-attributed.
   */
  async acceptIdentityChange(userId: string): Promise<void> {
    // Every device of that user shared the old identity key, so all of their
    // sessions are stale — not just the one that raised the alarm.
    for (const [key, session] of [...this.#sessions]) {
      if (session.peerUserId !== userId) continue;
      this.#sessions.delete(key);
      await this.#store.remove(STORAGE_KEYS.session(userId, session.peerDeviceId));
    }
    this.#deviceCache.delete(userId);
    const contact = this.#contacts.get(userId);
    if (contact) await this.#saveContact({ ...contact, identityKey: undefined, verified: false });
  }

  // -------------------------------------------------------------------------
  // Persistence helpers
  // -------------------------------------------------------------------------

  async #persistIdentity(): Promise<void> {
    if (this.#identity) {
      await this.#store.set(STORAGE_KEYS.identity, JSON.stringify(serializeIdentity(this.#identity)));
    }
  }

  async #persistSession(session: SessionRecord): Promise<void> {
    await this.#store.set(
      STORAGE_KEYS.session(session.peerUserId, session.peerDeviceId),
      JSON.stringify(serializeSession(session)),
    );
  }

  async #saveContact(contact: Contact): Promise<Contact> {
    const isNew = !this.#contacts.has(contact.id);
    const merged = { ...this.#contacts.get(contact.id), ...contact };
    this.#contacts.set(contact.id, merged);
    await this.#store.set(STORAGE_KEYS.contact(contact.id), JSON.stringify(merged));
    // The watch list is sent once per connection, so a contact added after we
    // connected would never report presence until the next reconnect.
    if (isNew) this.#socket?.watch([...this.#contacts.keys()]);
    this.#emit('contact', merged);
    return merged;
  }

  async #touchContact(userId: string, patch: Partial<Contact>): Promise<void> {
    const contact = this.#contacts.get(userId);
    if (contact) await this.#saveContact({ ...contact, ...patch });
  }

  async #saveConversation(conversation: Conversation): Promise<Conversation> {
    this.#conversations.set(conversation.id, conversation);
    await this.#store.set(STORAGE_KEYS.conversation(conversation.id), JSON.stringify(conversation));
    this.#emit('conversation', conversation);
    return conversation;
  }

  async #saveMessage(message: Message): Promise<void> {
    await this.#store.set(
      STORAGE_KEYS.message(message.conversationId, message.ts, message.id),
      JSON.stringify(message),
    );
  }

  /** Find a stored message by id and rewrite it. Ids are not in the key prefix. */
  async #updateMessage(
    conversationId: string,
    messageId: string,
    update: (message: Message) => Message,
  ): Promise<void> {
    const keys = await this.#store.keys(STORAGE_KEYS.messagePrefix(conversationId));
    const key = keys.find((k) => k.endsWith(`:${messageId}`));
    if (!key) return;
    const raw = await this.#store.get(key);
    if (!raw) return;

    const updated = update(JSON.parse(raw) as Message);
    await this.#store.set(key, JSON.stringify(updated));
    this.#emit('message-updated', updated);
  }

  async #applyReaction(
    conversationId: string,
    targetId: string,
    emoji: string,
    userId: string,
    remove = false,
  ): Promise<void> {
    await this.#updateMessage(conversationId, targetId, (message) => {
      const reactions = { ...(message.reactions ?? {}) };
      const users = new Set(reactions[emoji] ?? []);
      if (remove) users.delete(userId);
      else users.add(userId);

      if (users.size === 0) delete reactions[emoji];
      else reactions[emoji] = [...users];
      return { ...message, reactions };
    });
  }

  async #applyDelete(conversationId: string, targetId: string): Promise<void> {
    await this.#updateMessage(conversationId, targetId, (message) => ({
      ...message,
      deleted: true,
      content: { kind: 'text', text: '' },
    }));
  }

  #requireConversation(id: string): Conversation {
    const conversation = this.#conversations.get(id);
    if (!conversation) throw new Error(`no conversation ${id}`);
    return conversation;
  }

  // -------------------------------------------------------------------------
  // Acks
  // -------------------------------------------------------------------------

  #queueAck(id: string): void {
    this.#pendingAcks.push(id);
    if (this.#pendingAcks.length >= 50) {
      this.#flushAcks();
      return;
    }
    this.#ackTimer ??= setTimeout(() => this.#flushAcks(), 250);
  }

  #flushAcks(): void {
    if (this.#ackTimer) {
      clearTimeout(this.#ackTimer);
      this.#ackTimer = undefined;
    }
    if (this.#pendingAcks.length === 0) return;
    const ids = this.#pendingAcks;
    this.#pendingAcks = [];
    this.#socket?.ack(ids);
  }
}

/**
 * A short, stable tag for a pepper — not a secret, just a cache namespace.
 *
 * Reusing the pepper itself as part of a storage key would write a server secret
 * into local storage in the clear for no reason.
 */
function pepperFingerprint(pepper: string): string {
  return discoveryCacheKey('', pepper).replace(/[^A-Za-z0-9]/g, '').slice(0, 16);
}

function previewOf(content: MessageContent): string {
  switch (content.kind) {
    case 'text':
      return content.text.slice(0, 120);
    case 'media':
      return content.text?.slice(0, 120) || `📎 ${content.attachment.name}`;
    case 'group-update':
      return content.name ? `הקבוצה נקראת "${content.name}"` : 'עדכון קבוצה';
    default:
      return '';
  }
}
